// src/common/chart/MonthSalesChart.js
import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { getRequest } from "../../apis/ApiInstence";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toMonthIndex = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    const idx = Math.max(1, Math.min(12, Math.trunc(value))) - 1;
    return idx;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      const num = Number.parseInt(trimmed, 10);
      if (Number.isFinite(num)) {
        return Math.max(1, Math.min(12, num)) - 1;
      }
    }
    const short = trimmed.slice(0, 3).toLowerCase();
    const idx = MONTHS.map((m) => m.toLowerCase()).indexOf(short);
    return idx;
  }

  return -1;
};

const normalizeSeriesData = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  if (typeof raw[0] === "number") return raw;

  if (typeof raw[0] === "string") {
    return raw.map((v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    });
  }

  if (Array.isArray(raw[0])) return raw;

  if (typeof raw[0] === "object" && raw[0] !== null) {
    const byMonth = new Array(12).fill(0);
    let hasMonth = false;

    raw.forEach((item) => {
      const monthValue =
        item.month ??
        item.monthIndex ??
        item.monthNo ??
        item.month_number ??
        item.monthName ??
        item.month_name ??
        item.name ??
        item.label;
      const idx = toMonthIndex(monthValue);
      if (idx >= 0) {
        hasMonth = true;
        const value =
          item.total ??
          item.totalSales ??
          item.total_sales ??
          item.totalSale ??
          item.total_sale ??
          item.sales ??
          item.salesTotal ??
          item.sales_total ??
          item.salesAmount ??
          item.sales_amount ??
          item.amount ??
          item.amountTotal ??
          item.amount_total ??
          item.value ??
          item.count ??
          item.sum ??
          item.y;
        const num = Number(value);
        if (Number.isFinite(num)) {
          byMonth[idx] = num;
        }
      }
    });

    if (hasMonth) return byMonth;

    const fallback = raw.map((item) => {
      if (item && typeof item === "object") {
        const value =
          item.total ??
          item.totalSales ??
          item.total_sales ??
          item.totalSale ??
          item.total_sale ??
          item.sales ??
          item.salesTotal ??
          item.sales_total ??
          item.salesAmount ??
          item.sales_amount ??
          item.amount ??
          item.amountTotal ??
          item.amount_total ??
          item.value ??
          item.count ??
          item.sum ??
          item.y;
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      }
      const num = Number(item);
      return Number.isFinite(num) ? num : 0;
    });

    const hasAny = fallback.some((v) => v !== 0);
    return hasAny ? fallback : [];
  }

  return [];
};

const normalizeOhlcData = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  if (Array.isArray(raw[0])) {
    return raw
      .map((row) =>
        row
          .map((v) => Number(v))
          .filter((v) => typeof v === "number" && Number.isFinite(v))
      )
      .map((row) => (row.length >= 4 ? row.slice(row.length - 4) : row))
      .filter((row) => row.length >= 4);
  }

  if (typeof raw[0] === "object" && raw[0] !== null) {
    const byMonth = new Array(12).fill(null);
    let hasMonth = false;

    raw.forEach((item) => {
      const monthValue =
        item.month ??
        item.monthIndex ??
        item.monthNo ??
        item.month_number ??
        item.monthName ??
        item.month_name ??
        item.name ??
        item.label;
      const idx = toMonthIndex(monthValue);
      const open = Number(item.open ?? item.o ?? item.opening);
      const high = Number(item.high ?? item.h ?? item.highest);
      const low = Number(item.low ?? item.l ?? item.lowest);
      const close = Number(item.close ?? item.c ?? item.closing);

      if (
        idx >= 0 &&
        [open, high, low, close].every((v) => typeof v === "number")
      ) {
        hasMonth = true;
        byMonth[idx] = [open, high, low, close];
      }
    });

    if (hasMonth) {
      return byMonth.filter((row) => Array.isArray(row) && row.length === 4);
    }
  }

  return [];
};

const MonthSalesChart = ({ title, data, type = "column", height = 440 }) => {
  const [salesData, setSalesData] = useState([]);

  const fetchProducts = async () => {
    try {
      const res = await getRequest("order/monthSalesData");
      console.log("Month-wise sales data fetched successfully:", res?.data?.data);
      setSalesData(res?.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch products:", err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const rawData = salesData?.length ? salesData : data;
  const wantsCandle = type === "candlestick";

  const { seriesData, effectiveType } = useMemo(() => {
    if (wantsCandle) {
      const ohlc = normalizeOhlcData(rawData);
      if (ohlc.length) {
        return { seriesData: ohlc, effectiveType: "candlestick" };
      }
      console.warn(
        "Candlestick requested but OHLC data not found. Falling back to column chart."
      );
      return {
        seriesData: normalizeSeriesData(rawData),
        effectiveType: "column",
      };
    }
    return {
      seriesData: normalizeSeriesData(rawData),
      effectiveType: type,
    };
  }, [rawData, type, wantsCandle]);

  const options = {
    chart: {
      type: effectiveType,
      height,
    },
    title: {
      text: title || "Month-wise Sales Data",
    },
    subtitle: {
      text: "Sales in INR",
    },
    xAxis: {
      categories: MONTHS,
      crosshair: true,
    },
    yAxis: {
      min: 0,
      title: {
        text: "Sales (INR)",
      },
    },
    tooltip: {
      headerFormat: "<span style='font-size:10px'>{point.key}</span><table>",
      pointFormat:
        "<tr><td style='color:{series.color};padding:0'>{series.name}: </td>" +
        "<td style='padding:0'><b>Rs {point.y}</b></td></tr>",
      footerFormat: "</table>",
      shared: true,
      useHTML: true,
    },
    plotOptions: {
      column: {
        pointPadding: 0.2,
        borderWidth: 0,
      },
    },
    series: [
      {
        name: "Sales",
        type: effectiveType,
        data: seriesData,
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default MonthSalesChart;
